import { useState, useEffect, useCallback } from 'react';
import toast from 'react-hot-toast';
import { leadsAPI } from '../services/api';
import { Search, Download, Archive, Trash2, ChevronLeft, ChevronRight, X, Mail, Phone } from 'lucide-react';
import { format } from 'date-fns';

const STATUS_COLORS = { new:'green', contacted:'blue', qualified:'amber', converted:'green', lost:'red' };
const STATUSES = ['new','contacted','qualified','converted','lost'];

function LeadModal({ lead, onClose, onUpdate }) {
  const [form, setForm] = useState({ status: lead.status, notes: lead.notes||'' });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await leadsAPI.update(lead._id, form);
      onUpdate(data.lead);
      toast.success('Lead updated');
      onClose();
    } catch { toast.error('Update failed'); }
    finally { setSaving(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target===e.currentTarget && onClose()}>
      <div className="modal">
        <div className="modal-header">
          <span className="modal-title">{lead.name || 'Unknown visitor'}</span>
          <button className="btn btn-ghost btn-icon" onClick={onClose}><X size={18}/></button>
        </div>
        <div style={{ display:'flex',flexDirection:'column',gap:14 }}>
          {/* Contact info */}
          <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:12 }}>
            {lead.email && <a href={`mailto:${lead.email}`} style={{ display:'flex',alignItems:'center',gap:8,fontSize:13,color:'var(--brand)',textDecoration:'none',background:'var(--bg3)',padding:'8px 12px',borderRadius:'var(--radius)' }}><Mail size={14}/>{lead.email}</a>}
            {lead.phone && <a href={`tel:${lead.phone}`} style={{ display:'flex',alignItems:'center',gap:8,fontSize:13,color:'var(--text)',textDecoration:'none',background:'var(--bg3)',padding:'8px 12px',borderRadius:'var(--radius)' }}><Phone size={14}/>{lead.phone}</a>}
          </div>
          {lead.topic && <div style={{ fontSize:13,color:'var(--text2)',background:'var(--bg3)',padding:'10px 14px',borderRadius:'var(--radius)' }}><strong>Topic:</strong> {lead.topic}</div>}
          {lead.summary && <div style={{ fontSize:13,color:'var(--text2)',background:'var(--bg3)',padding:'10px 14px',borderRadius:'var(--radius)' }}><strong>Summary:</strong> {lead.summary}</div>}
          {lead.source && <div style={{ fontSize:12,color:'var(--text3)' }}>Source: {lead.source}</div>}

          <div className="form-group">
            <label className="form-label">Status</label>
            <select className="select" value={form.status} onChange={e => setForm(v=>({...v,status:e.target.value}))}>
              {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Notes</label>
            <textarea className="textarea" rows={3} placeholder="Add notes about this lead..."
              value={form.notes} onChange={e => setForm(v=>({...v,notes:e.target.value}))}/>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={saving}>{saving?'Saving…':'Save'}</button>
        </div>
      </div>
    </div>
  );
}

export default function Leads() {
  const [leads, setLeads]     = useState([]);
  const [total, setTotal]     = useState(0);
  const [page, setPage]       = useState(1);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [status, setStatus]   = useState('');
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    leadsAPI.list({ page, limit:20, search, status: status||undefined })
      .then(r => { setLeads(r.data.leads||[]); setTotal(r.data.total||0); })
      .catch(() => toast.error('Failed to load leads'))
      .finally(() => setLoading(false));
  }, [page, search, status]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = async () => {
    try {
      const { data } = await leadsAPI.export({ status: status||undefined });
      const url = URL.createObjectURL(new Blob([data]));
      const a = document.createElement('a'); a.href=url; a.download='wispy-leads.csv'; a.click();
      URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch { toast.error('Export failed'); }
  };

  const archive = async (lead, e) => {
    e.stopPropagation();
    try {
      await leadsAPI.archive(lead._id);
      setLeads(l => l.filter(x => x._id !== lead._id));
      toast.success('Lead archived');
    } catch { toast.error('Failed'); }
  };

  const deleteLead = async (lead, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this lead?')) return;
    try {
      await leadsAPI.delete(lead._id);
      setLeads(l => l.filter(x => x._id !== lead._id));
      toast.success('Lead deleted');
    } catch { toast.error('Failed'); }
  };

  const onUpdate = (updated) => {
    setLeads(l => l.map(x => x._id===updated._id ? updated : x));
  };

  const pages = Math.ceil(total/20);

  return (
    <div>
      <div className="page-header" style={{ display:'flex',justifyContent:'space-between',alignItems:'flex-start',flexWrap:'wrap',gap:12 }}>
        <div><h1>Leads</h1><p>{total} total leads captured</p></div>
        <button className="btn btn-secondary" onClick={exportCSV}><Download size={15}/> Export CSV</button>
      </div>

      {/* Filters */}
      <div style={{ display:'flex',gap:12,marginBottom:20,flexWrap:'wrap' }}>
        <div style={{ position:'relative',flex:1,minWidth:220 }}>
          <Search size={15} style={{ position:'absolute',left:12,top:'50%',transform:'translateY(-50%)',color:'var(--text3)' }}/>
          <input className="input" placeholder="Search by name, email, phone…" style={{ paddingLeft:36 }}
            value={search} onChange={e => { setSearch(e.target.value); setPage(1); }}/>
        </div>
        <select className="select" style={{ width:160 }} value={status} onChange={e => { setStatus(e.target.value); setPage(1); }}>
          <option value="">All statuses</option>
          {STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
        </select>
      </div>

      <div className="card" style={{ padding:0, overflow:'hidden' }}>
        {loading ? (
          <div style={{ display:'flex',justifyContent:'center',padding:60 }}><div className="spinner"/></div>
        ) : leads.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize:40 }}>📬</div>
            <h3>No leads yet</h3>
            <p>Leads are captured automatically when visitors share their contact info in a chat.</p>
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Name</th><th>Contact</th><th>Bot</th><th>Topic</th><th>Status</th><th>Date</th><th></th>
                </tr>
              </thead>
              <tbody>
                {leads.map(lead => (
                  <tr key={lead._id} style={{ cursor:'pointer' }} onClick={() => setSelected(lead)}>
                    <td style={{ fontWeight:500 }}>{lead.name || <span style={{ color:'var(--text3)' }}>Unknown</span>}</td>
                    <td>
                      <div style={{ fontSize:13 }}>{lead.email || '—'}</div>
                      {lead.phone && <div style={{ fontSize:12,color:'var(--text3)' }}>{lead.phone}</div>}
                    </td>
                    <td style={{ fontSize:13,color:'var(--text2)' }}>{lead.bot?.name || '—'}</td>
                    <td style={{ fontSize:12,color:'var(--text2)',maxWidth:180,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{lead.topic || '—'}</td>
                    <td><span className={`badge badge-${STATUS_COLORS[lead.status]||'gray'}`}>{lead.status}</span></td>
                    <td style={{ fontSize:12,color:'var(--text3)',whiteSpace:'nowrap' }}>{format(new Date(lead.createdAt),'MMM d, yyyy')}</td>
                    <td>
                      <div style={{ display:'flex',gap:4 }} onClick={e=>e.stopPropagation()}>
                        <button className="btn btn-ghost btn-icon btn-sm" title="Archive" onClick={e=>archive(lead,e)}><Archive size={13}/></button>
                        <button className="btn btn-ghost btn-icon btn-sm" style={{ color:'var(--red)' }} title="Delete" onClick={e=>deleteLead(lead,e)}><Trash2 size={13}/></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {pages > 1 && (
          <div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:12,padding:'14px 20px',borderTop:'1px solid var(--border)' }}>
            <button className="btn btn-ghost btn-sm" disabled={page===1} onClick={()=>setPage(p=>p-1)}><ChevronLeft size={15}/></button>
            <span style={{ fontSize:13,color:'var(--text2)' }}>Page {page} of {pages}</span>
            <button className="btn btn-ghost btn-sm" disabled={page===pages} onClick={()=>setPage(p=>p+1)}><ChevronRight size={15}/></button>
          </div>
        )}
      </div>

      {selected && <LeadModal lead={selected} onClose={() => setSelected(null)} onUpdate={onUpdate}/>}
    </div>
  );
}
